import { Alert, Status } from "../../types";
import { alertEventsService } from "./alertEventsService";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";
import { createEntityId } from "./internal/entityId";

const API_PATH = "/v1/alarm";

const normalizePutAlertsResponse = (
  payload: Alert | Alert[] | undefined,
  fallback: Alert[],
): Alert[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload) {
    return [payload];
  }

  return fallback;
};

const putAlerts = async (alerts: Alert[]): Promise<Alert[]> => {
  const payload = await sigv4ApiClient.put<Alert[] | Alert | undefined>(
    API_PATH,
    alerts,
  );

  return normalizePutAlertsResponse(payload, alerts);
};

export const alertService = {
  create: async (input: Omit<Alert, "id">): Promise<Alert> => {
    const created = { ...input, id: createEntityId() };
    const [persisted = created] = await putAlerts([created]);
    return persisted;
  },

  list: async (): Promise<Alert[]> => {
    return sigv4ApiClient.list<Alert>(API_PATH);
  },

  getStatus: async (alertId: string): Promise<Status> => {
    const alarm = await sigv4ApiClient.get<Alert & { status?: Status }>(
      `${API_PATH}/${encodeURIComponent(alertId)}`,
    );
    if (alarm?.status) {
      return alarm.status;
    }

    const events = (await alertEventsService.list(alertId)).sort(
      (a, b) => b.timestamp - a.timestamp,
    );
    return events[0]?.status ?? "healthy";
  },

  get: async (id: string): Promise<Alert | undefined> => {
    return sigv4ApiClient.get<Alert>(`${API_PATH}/${encodeURIComponent(id)}`);
  },

  update: async (id: string, next: Omit<Alert, "id">): Promise<Alert> => {
    const updatedAlert = { ...next, id };
    const [persisted = updatedAlert] = await putAlerts([updatedAlert]);
    return persisted;
  },

  patch: async (
    id: string,
    patch: Partial<Omit<Alert, "id">>,
  ): Promise<Alert> => {
    const current = await alertService.get(id);
    if (!current) {
      throw new Error(`Alert with id "${id}" not found`);
    }

    const updatedAlert = { ...current, ...patch, id };
    const [persisted = updatedAlert] = await putAlerts([updatedAlert]);
    return persisted;
  },

  delete: async (id: string): Promise<boolean> => {
    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(id)}`);
    await alertEventsService.clear(id);
    return true;
  },
};
